import Input from '@/components/Input.jsx';
import Panel from '@/components/panel/Panel.jsx';
import { BiSave, BiText, MdMemory, MdOutlinePublic, RxLapTimer } from '@/components/react-icons/main';
import debounce from '@/lib/debounce.js';
import sendToast from '@/lib/sendToast.js';
import stepActions from '@/screens/workflows/redux/actions/stepActions.js';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors.js';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import ArgumentLabel from '../ArgumentLabel.jsx';
import ArgumentWrapper from '../ArgumentWrapper.jsx';
import SliderClose from './SliderClose.jsx';
import SliderHeader from './SliderHeader.jsx';
import SliderTitle from './SliderTitle.jsx';
import { randomId } from '@shared/utils/randomId.js';
import { memoryExpiresOnOptions, memorySavesOnOptions, memoryScopeOptions, embeddedMemoryScopeOptions } from '@shared/schema/aiSchemas/passive/workflowSchemas/memorySchema.js';
import useMemoryKeyInputValidation from '@/hooks/useMemoryKeyInputValidation.js';
import { openDocDrawer } from '@/redux/actions/uiActions/drawerActions.js';

const MemorySlider = ({ onClose, disabled = false }) => {
  const [argsKeySeed] = useState(randomId());
  const isEditingRef = useRef(false);
  const lastKeyValueRef = useRef('');

  const usesEnvironments = useSelector(workflowScreenSelectors.activeWorkflowUsesEnvironments);
  const isEmbedded = useSelector(workflowScreenSelectors.activeWorkflowIsEmbedded);
  const scopeOptions = isEmbedded ? embeddedMemoryScopeOptions : memoryScopeOptions;
  const memory = useSelector((state) => workflowScreenSelectors.activeWorkflowStep(state)?.options?.memory) || [];
  const memoryId = useSelector((state) => state?.ui?.argumentOptionSlider?.memoryId);
  const currentMemory = memory.find((m) => m.id === memoryId) || {};
  const [localMemory, setLocalMemory] = useState(currentMemory);
  const { memoryKey, memoryKeyDescription, invalidKey } = useMemoryKeyInputValidation({ key: localMemory.key, scope: localMemory.scope });

  const workflowScopeValue = localMemory.scope || 'workflow';
  const workflowScopeInfo = 'It is recommended you set the scope to "This Environment".';

  useEffect(() => {
    if (!isEditingRef.current) {
      setLocalMemory(currentMemory);
      lastKeyValueRef.current = currentMemory.key || '';
    }
  }, [memoryId, currentMemory]);

  const getLabel = (value, options) => {
    const option = options.find((o) => o.value === value);
    return option?.label || value;
  };

  const updateMemory = useMemo(() => {
    return debounce((updates) => {
      stepActions.set('options.memory', (options) => (Array.isArray(options) ? options : []).map((option) => (option.id === memoryId ? { ...option, ...updates } : option)));
      isEditingRef.current = false;
    }, 300);
  }, [memoryId]);

  const handleChange = (field, value) => {
    if (field !== 'key' && !localMemory.key) {
      sendToast({ message: 'Please enter a key for this memory', type: 'error' });
      return;
    }

    if (field === 'key') {
      isEditingRef.current = true;
      lastKeyValueRef.current = value;
    }

    const updates = { [field]: value };
    setLocalMemory((prev) => ({ ...prev, ...updates }));
    updateMemory(updates);
  };

  const handleKeyBlur = () => {
    isEditingRef.current = false;
  };

  const handleDelete = async () => {
    stepActions.set('options.memory', (options) => (Array.isArray(options) ? options : []).filter((option) => option.id !== memoryId));
    onClose();
  };

  const handleLearnMore = () => {
    openDocDrawer({ docId: 'workflowStepMemory', highlightedElementId: undefined });
    onClose();
  };

  return (
    <>
      <SliderHeader onClose={onClose} showDelete onDelete={handleDelete} disabled={disabled} />
      <SliderTitle icon={MdMemory} title="Edit Memory" description="Configure memory settings for this entry." helpIconContext="workflowStepMemoryContext" />
      <Panel.Section title="Key" collapsable={false}>
        <Input disabled={disabled} value={memoryKey} className={invalidKey ? 'error' : ''} onChange={(e) => handleChange('key', e.target.value)} onBlur={handleKeyBlur} placeholder="Enter memory key..." description={memoryKeyDescription} />
      </Panel.Section>
      <Panel.Section title="Options" className={!localMemory.key ? 'disabled' : ''} collapsable={false}>
        <Panel.LexicalInput //
          key={`${argsKeySeed}-${localMemory.value || 'empty'}`}
          label="Value"
          icon={BiText}
          value={localMemory.value}
          onChange={(value) => handleChange('value', value)}
          placeholder="Set a Value"
          disabled={disabled || !localMemory.key}
        />
        <ArgumentWrapper>
          <ArgumentLabel label="Scope" icon={MdOutlinePublic} />
          <Panel.ToggleButton key={argsKeySeed} options={scopeOptions} value={workflowScopeValue} onChange={(value) => handleChange('scope', value)} disabled={disabled || !localMemory.key} fullWidth size="sm" />
          {usesEnvironments && workflowScopeValue === 'workflow' && (
            <Panel.Alert
              title={
                <>
                  {workflowScopeInfo} <br />
                  <span className="learn-more" onClick={handleLearnMore}>
                    Learn more.
                  </span>
                </>
              }
              variant={'info'}
            />
          )}
        </ArgumentWrapper>
        <ArgumentWrapper>
          <ArgumentLabel label="Saves On" icon={BiSave} />
          <Panel.ToggleButton key={argsKeySeed} options={memorySavesOnOptions} value={localMemory.savesOn || 'success'} onChange={(value) => handleChange('savesOn', value)} disabled={disabled || !localMemory.key} fullWidth size="sm" />
        </ArgumentWrapper>
        <Panel.Select key={argsKeySeed} label="Expires After" icon={RxLapTimer} value={getLabel(localMemory.expiresOn || 'never', memoryExpiresOnOptions)} onChange={({ value }) => handleChange('expiresOn', value)} placeholder="Select when the value will expire" options={memoryExpiresOnOptions} disabled={disabled || !localMemory.key} />
      </Panel.Section>
      <SliderClose onClose={onClose} />
    </>
  );
};

export default MemorySlider;
